import mixpanel from 'mixpanel-browser';

class MixpanelPluginV2 {
  /**
   * @param {Object} options
   * @param {string | null} [options.token]
   * @param {boolean} [options.enabled]
   * @param {boolean} [options.debug]
   * @param {Object} [options.logger]
   */
  constructor({ token = null, enabled = true, debug = false, logger = console } = {}) {
    this.client = mixpanel;
    this.token = token;
    this.enabled = enabled;
    this.debug = debug;
    this.logger = logger;
    this.isInitialized = false;
  }

  /**
   * Reconfigure plugin at runtime.
   * @param {Object} options
   * @param {string | null} [options.token]
   * @param {boolean} [options.enabled]
   * @param {boolean} [options.debug]
   */
  configure({ token, enabled, debug } = {}) {
    if (token !== undefined) {
      this.token = token;
    }
    if (enabled !== undefined) {
      this.enabled = enabled;
    }
    if (debug !== undefined) {
      this.debug = debug;
    }
  }

  /**
   * Initialize analytics SDK.
   * @param {Object} options
   * @param {string} [options.token]
   * @param {Object} [options.initOptions]
   * @returns {boolean}
   */
  init({ token, initOptions = {} } = {}) {
    if (token !== undefined) {
      this.token = token;
    }

    if (!this.#canUseClient() || !this.token || typeof this.client.init !== 'function') {
      this.isInitialized = false;
      if (this.debug && this.#canUseClient() && !this.token) {
        this.logger.error('[MixpanelPluginV2] init skipped: no token (events will not be sent)');
      }
      return false;
    }

    try {
      this.client.init(this.token, initOptions);
      this.isInitialized = true;
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] init error', error);
      this.isInitialized = false;
    }
    return this.isInitialized;
  }

  /**
   * Track a generic analytics event.
   * @param {string} event
   * @param {Object} [payload]
   */
  track(event, payload = {}) {
    const canTrack = this.#canTrack() && typeof this.client.track === 'function';
    if (this.debug) {
      this.logger.info('[MixpanelPluginV2] track', {
        event,
        payload,
        skipped: !canTrack
      });
    }

    if (!canTrack) {
      return;
    }

    try {
      this.client.track(event, payload);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] track error', error);
    }
  }

  /**
   * Set distinct user identity.
   * @param {string} distinctId
   */
  identify(distinctId) {
    if (!this.#canTrack() || typeof this.client.identify !== 'function' || !distinctId) {
      return;
    }
    try {
      this.client.identify(distinctId);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] identify error', error);
    }
  }

  /**
   * Set user profile properties.
   * Maps to mixpanel.people.set when available.
   * @param {Object} properties
   */
  setUserProperties(properties = {}) {
    if (!this.#canTrack() || !this.client.people || typeof this.client.people.set !== 'function') {
      return;
    }

    try {
      this.client.people.set(properties);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] setUserProperties error', error);
    }
  }

  /**
   * Register super properties for future events.
   * Maps to mixpanel.register.
   * @param {Object} properties
   */
  setProperties(properties = {}) {
    if (!this.#canTrack() || typeof this.client.register !== 'function') {
      return;
    }

    try {
      this.client.register(properties);
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] setProperties error', error);
    }
  }

  /**
   * Set group-level properties.
   * Maps to mixpanel.get_group(...).set(...).
   * @param {string} groupKey
   * @param {string | number} groupId
   * @param {Object} properties
   */
  setGroupProperties(groupKey, groupId, properties = {}) {
    if (!this.#canTrack() || typeof this.client.get_group !== 'function') {
      return;
    }

    try {
      const group = this.client.get_group(groupKey, groupId);
      if (group && typeof group.set === 'function') {
        group.set(properties);
      }
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] setGroupProperties error', error);
    }
  }

  /**
   * Register org super properties (org_id, org_name).
   * @param {{ id?: string | null, name?: string | null }} org
   */
  setOrgProperties(org) {
    this.setProperties({ org_id: org?.id ?? null, org_name: org?.name ?? null });
  }

  /**
   * Register warehouse super properties (warehouse_id, warehouse_name).
   * @param {{ id?: string | null, name?: string | null }} warehouse
   */
  setWarehouseProperties(warehouse) {
    this.setProperties({
      warehouse_id: warehouse?.id ?? null,
      warehouse_name: warehouse?.name ?? null
    });
  }

  /**
   * Reset the Mixpanel instance, clearing user identity and super properties.
   * @returns {void}
   */
  reset() {
    if (!this.#canUseClient() || !this.isInitialized || typeof this.client.reset !== 'function') {
      return;
    }

    try {
      this.client.reset();
    } catch (error) {
      this.logger.error('[MixpanelPluginV2] reset error', error);
    }
  }

  #canUseClient() {
    return this.enabled && !!this.client;
  }

  #canTrack() {
    return this.#canUseClient() && this.isInitialized;
  }
}

export default MixpanelPluginV2;
